import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface PendingRating {
  bookingId: string;
  otherPersonId: string;
  otherPersonName: string;
  experienceTitle: string;
  sessionDate: string;
  sessionPrice: number;
  raterRole: "coach" | "learner";
  coachId: string;
}

export const usePendingRatings = () => {
  const [pendingRatings, setPendingRatings] = useState<PendingRating[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPendingRatings = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setPendingRatings([]);
        setLoading(false);
        return;
      }

      const userId = session.user.id;

      // Get all completed bookings where user is coach or learner
      const { data: bookings, error: bookingsError } = await supabase
        .from("bookings")
        .select(`
          id,
          coach_id,
          learner_id,
          scheduled_at,
          total_price,
          experience_id,
          experiences:experience_id (title)
        `)
        .eq("status", "completed")
        .or(`coach_id.eq.${userId},learner_id.eq.${userId}`);

      if (bookingsError || !bookings?.length) {
        setPendingRatings([]);
        setLoading(false);
        return;
      }

      // Get existing ratings by this user
      const { data: existingRatings } = await supabase
        .from("session_ratings" as any)
        .select("booking_id")
        .eq("rater_id", userId);

      const ratedBookingIds = new Set(
        (existingRatings || []).map((r: any) => r.booking_id)
      );

      // Filter to unrated bookings
      const unrated = bookings.filter((b) => !ratedBookingIds.has(b.id));

      if (!unrated.length) {
        setPendingRatings([]);
        setLoading(false);
        return;
      }

      // Get names of the other people
      const otherIds = unrated.map((b) =>
        b.coach_id === userId ? b.learner_id : b.coach_id
      );

      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .in("id", otherIds);

      const nameMap = new Map(
        (profiles || []).map((p) => [
          p.id,
          p.full_name?.trim() || p.email?.split("@")[0] || "Your partner",
        ])
      );

      const pending: PendingRating[] = unrated.map((b) => {
        const isCoach = b.coach_id === userId;
        const otherId = isCoach ? b.learner_id : b.coach_id;
        return {
          bookingId: b.id,
          otherPersonId: otherId,
          otherPersonName: nameMap.get(otherId) || "Unknown",
          experienceTitle: (b.experiences as any)?.title || "Session",
          sessionDate: b.scheduled_at,
          sessionPrice: b.total_price,
          raterRole: isCoach ? "coach" : "learner",
          coachId: b.coach_id,
        };
      });

      setPendingRatings(pending);
    } catch (err) {
      console.error("Error fetching pending ratings:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingRatings();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      fetchPendingRatings();
    });

    return () => subscription.unsubscribe();
  }, []);

  return { pendingRatings, loading, refetch: fetchPendingRatings };
};
